import axiosInstance from '../axios/axios-instance';
import { DeleteUserRequest } from 'shared/interfaces/auth/delete-user-request.interface';
import { DeleteUserResponse } from 'shared/interfaces/auth/delete-user-response.interface';
import { RequestEmailChangeResponse } from 'shared/interfaces/auth/request-email-change-response.interface';
import { StreakProgressProps } from 'shared/interfaces/streak-progress/streak-progress.interface';
import { MyAccountDetailsDto } from 'shared/interfaces/user/my-account-details.dto';
import { ActivityLogHistoryDto } from 'shared/interfaces/user/my-rewards/activity-log-history.dto';
import { PostRegistrationUserInitialDetailsResponseDto } from 'shared/interfaces/user/post-registration-user-initial-details-response.dto';
import { UserDeletionReasonRequest } from 'shared/interfaces/user/user-deletion/user-deletion-reason.request.dto';
export const user = {
  /**
   * Retrieves the initial details of a user after registration.
   * @returns A promise that resolves to the initial details of the user.
   */
  async getPostRegistrationUserInitialDetails(): Promise<PostRegistrationUserInitialDetailsResponseDto> {
    const response = await axiosInstance.get<PostRegistrationUserInitialDetailsResponseDto>(
      '/user/post-registration-user-initial-details',
      {
        headers: { 'user-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone },
      }
    );
    return response.data;
  },

  /**
   * Updates the post-registration details of a user.
   * @param firstName - The first name of the user.
   * @param lastName - The last name of the user.
   * @param country - The country of the user.
   */
  async updateUserPostRegistrationDetails(
    firstName: string,
    lastName: string,
    country: string,
    isConsentGiven: boolean
  ): Promise<void> {
    await axiosInstance.patch(
      '/user/post-registration-details',
      {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        country,
        isConsentGiven,
      },
      {
        headers: { 'user-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone },
      }
    );
  },

  /**
   * Retrieves the user's account information.
   * @returns A promise that resolves to the user's account information.
   */
  async getMyAccountDetails(): Promise<MyAccountDetailsDto> {
    const response = await axiosInstance.get<MyAccountDetailsDto>('/user/my-account-details');
    return response.data;
  },

  /**
   * Records that the current user installed or launched the installed PWA.
   */
  async recordPwaInstallation(): Promise<void> {
    await axiosInstance.patch('/user/pwa-installation');
  },

  // [DELETE IN PROD-792]
  /** Fetches streak progress for NewStreakIntro. Backend pays compensation and sets NotifyAboutNewStreakSystem to false when needed. */
  async getStreakProgress(): Promise<StreakProgressProps | null> {
    const response = await axiosInstance.get<StreakProgressProps | null>('/user/streak-progress');
    return response.data;
  },
  // [END OF DELETION]

  /**
   * Updates the user's profile.
   * @param firstName - The first name of the user.
   * @param lastName - The last name of the user.
   */
  async updateUserProfile(
    firstName: string,
    lastName: string,
    email?: string,
    verisoulSessionId?: string
  ): Promise<RequestEmailChangeResponse | void> {
    const response = await axiosInstance.patch<RequestEmailChangeResponse>(
      '/user/profile',
      {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email?.trim(),
      },
      {
        headers: {
          'x-session-id': verisoulSessionId,
        },
      }
    );
    return response.data;
  },

  /**
   * Retrieves the user's points transaction history.
   * @param page - The page number to retrieve.
   * @param pageSize - The number of items per page.
   * @returns A promise that resolves to the user's points transaction history.
   */
  async getPointsTransactionHistory(page: number, pageSize: number): Promise<ActivityLogHistoryDto> {
    const response = await axiosInstance.get<ActivityLogHistoryDto>('/points-transaction/history', {
      params: {
        page,
        pageSize,
      },
    });
    return response.data;
  },

  /**
   * Creates a user deletion reason.
   * @param userDeletionReason - The user deletion reason request.
   */
  async saveUserDeletionReason(userDeletionReason: UserDeletionReasonRequest): Promise<void> {
    await axiosInstance.post('/user-deletion-reason', userDeletionReason);
  },

  /**
   * Deletes the user account.
   *
   * @returns A boolean indicating whether the user was deleted
   */
  async deleteUser(deleteUserRequest: DeleteUserRequest): Promise<DeleteUserResponse> {
    const response = await axiosInstance.delete<DeleteUserResponse>('/user', {
      withCredentials: true,
      data: deleteUserRequest,
    });
    return response.data;
  },
};
